import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { convertToRelativeGrade } from '../../utils/gradeUtils';
import { Modal, Button } from '../common';

function SingleFileUploadModal({ isOpen, onClose, exam, onSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();

  const phases = [
    { value: '11', label: '1학기 중간' },
    { value: '12', label: '1학기 기말' },
    { value: '13', label: '1학기 기타' },
    { value: '21', label: '2학기 중간' },
    { value: '22', label: '2학기 기말' },
    { value: '23', label: '2학기 기타' }
  ];

  // 파일 업로드
  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert('파일을 선택해주세요.');
      return;
    }

    if (!selectedFile.type.includes('pdf')) {
      alert('PDF 파일만 업로드 가능합니다.');
      return;
    }

    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/${exam.sxm_id}/file-upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
          },
          body: formData
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '파일 업로드에 실패했습니다.');
      }

      const result = await response.json();
      alert('파일 업로드가 완료되었습니다.');
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) { // 50MB
        alert('파일 크기는 50MB를 초과할 수 없습니다.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  if (!isOpen || !exam) return null;

  const phaseLabel = phases.find(p => p.value == exam.phase)?.label || exam.phase;
  
  // 학년 표시를 위한 헬퍼 함수
  const formatGradeDisplay = (absoluteGrade) => {
    const gradeInfo = convertToRelativeGrade(absoluteGrade);
    if (gradeInfo.schoolType === 1) {
      return `${gradeInfo.relativeGrade}학년 (초등)`;
    } else if (gradeInfo.schoolType === 2) {
      return `${gradeInfo.relativeGrade}학년 (중등)`;
    } else if (gradeInfo.schoolType === 3) {
      return `${gradeInfo.relativeGrade}학년 (고등)`;
    }
    return `${absoluteGrade}학년`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="파일 업로드"
      size="md"
      preventScroll={true}
      zIndex={9999950}
      isMobileView={window.innerWidth < 768}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleFileUpload}
            disabled={!selectedFile || isLoading}
            loading={isLoading}
            className="bg-primary hover:bg-[#3da575]"
          >
            업로드
          </Button>
        </>
      }
    >
      <div className="mb-6 rounded border border-blue-200 bg-blue-50 p-4">
        <h3 className="mb-2 font-semibold text-blue-800">시험 정보</h3>
        <div className="space-y-1 text-sm text-blue-700">
          <p><strong>ID:</strong> {exam.sxm_id}</p>
          <p><strong>학교:</strong> {exam.school_name}</p>
          <p><strong>년도:</strong> {exam.att_year}년</p>
          <p><strong>학년:</strong> {formatGradeDisplay(exam.school_grade)}</p>
          <p><strong>시험구분:</strong> {phaseLabel}</p>
          <p><strong>과목:</strong> {exam.subject || '미지정'}</p>
        </div>
      </div>
      
      <div className="rounded-3xl border-2 border-dashed border-gray-300 p-6 text-center">
        <input
          type="file"
          accept=".pdf"
          onChange={handleFileSelect}
          className="hidden"
          id="single-file-upload"
        />
        <label
          htmlFor="single-file-upload"
          className="flex cursor-pointer flex-col items-center"
        >
          <Upload className="mb-4 size-12 text-gray-400" />
          <p className="mb-2 text-lg font-medium text-gray-600">
            PDF 파일을 선택하세요
          </p>
          <p className="text-sm text-gray-500">
            최대 파일 크기: 50MB
          </p>
        </label>
      </div>

      {selectedFile && (
        <div className="mt-4">
          <h3 className="mb-2 font-semibold">선택된 파일</h3>
          <div className="rounded border bg-gray-50 p-3">
            <p className="text-sm font-medium">{selectedFile.name}</p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default SingleFileUploadModal; 